import { ActionRef } from 'further-types/lp/action';
import { PartnerTransferSection } from 'further-types/lp/partner-transfer';
import { AllocationRule } from 'further-types/lp/allocations';
import { ActionRefLabels } from 'further-ui/labels';
import api from 'lib/trpcClient';

type UseLoadPartnerTransferActionsParams = {
  partnerTransferId: string;
  vehicleId: string;
  partnerTransferSection: PartnerTransferSection;
};

const sectionActionRefs: Partial<
  Record<PartnerTransferSection, ReadonlyArray<ActionRef>>
> = {
  [PartnerTransferSection.Investments]: [ActionRef.Investment],
  [PartnerTransferSection.IncomeAndExpenses]: [ActionRef.IncomeOrExpense],
  [PartnerTransferSection.Realisations]: [ActionRef.Realisation],
};

export default function useLoadPartnerTransferActions({
  partnerTransferId,
  vehicleId,
  partnerTransferSection,
}: UseLoadPartnerTransferActionsParams) {
  const [actionAllocations] =
    api.partnerTransfers.getActionAllocations.useSuspenseQuery(
      {
        partnerTransferId,
        vehicleId,
      },
      {
        staleTime: 0,
        gcTime: 0,
      },
    );

  const allowedActionRefs = sectionActionRefs[partnerTransferSection];

  const filteredActionAllocations = allowedActionRefs
    ? actionAllocations.filter((action) =>
        allowedActionRefs.includes(action.actionRef),
      )
    : actionAllocations;

  return filteredActionAllocations.map((action) => ({
    actionId: action.id,
    actionRef: action.actionRef,
    actionType: ActionRefLabels[action.actionRef],
    counterpartyLabel: action.counterpartyLabel,
    ledgerDate: action.ledgerDate,
    totalAmountLcy: action.totalAmountLcy,
    allocationRule:
      action.allocationRule ?? AllocationRule.ByCommitmentAndClosingDate,
    originalAllocations: action.originalAllocations.map((allocation) => ({
      ...allocation,
      allocationAmountLcy: allocation.allocationAmountLcy ?? 0,
    })),
    newAllocations: action.newAllocations.map((allocation) => ({
      ...allocation,
      allocationAmountLcy: allocation.allocationAmountLcy ?? 0,
    })),
    linkedActions: action.linkedActions.map((linkedAction) => ({
      ...linkedAction,
      actionId: linkedAction.id,
      actionRef: linkedAction.actionRef,
      actionType: ActionRefLabels[linkedAction.actionRef],
      allocationRule:
        linkedAction.allocationRule ??
        AllocationRule.ByCommitmentAndClosingDate,
      originalAllocations: linkedAction.originalAllocations.map(
        (allocation) => ({
          ...allocation,
          allocationAmountLcy: allocation.allocationAmountLcy ?? 0,
        }),
      ),
      newAllocations: linkedAction.newAllocations.map((allocation) => ({
        ...allocation,
        allocationAmountLcy: allocation.allocationAmountLcy ?? 0,
      })),
    })),
  }));
}
